import { useEffect } from 'react';

const SITE_URL = 'https://tcubefitness.com';

const ORG_NODE = {
  '@type': 'MedicalBusiness',
  '@id': `${SITE_URL}/#organization`,
  name: 'TCUBE FITNESS',
  alternateName: 'TCUBE FITNESS™',
  url: SITE_URL,
  logo: 'https://customer-assets.emergentagent.com/job_8b2c67d7-f3f8-4dd8-ad33-921e8e7f1a96/artifacts/s6b7c1yc_transprant.png',
  image: 'https://customer-assets.emergentagent.com/job_8b2c67d7-f3f8-4dd8-ad33-921e8e7f1a96/artifacts/s6b7c1yc_transprant.png',
  description:
    'TCUBE FITNESS is a doctor-led preventive health and clinical nutrition platform offering medically supervised programs for fertility, pregnancy, postpartum, PMOS and lifestyle transformation.',
  medicalSpecialty: [
    'PreventiveMedicine',
    'Nutrition',
    'Obstetrics',
    'Gynecology',
  ],
  founder: {
    '@type': 'Person',
    name: 'Dr. Pooja Sree',
    jobTitle: 'General Medicine Physician · Sports Nutrition Expert',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+91-9515901777',
    contactType: 'customer service',
    areaServed: 'IN',
    availableLanguage: ['English', 'Telugu'],
  },
  sameAs: [
    'https://www.instagram.com/tcubefitness/',
    'https://www.instagram.com/layman_doctor/',
  ],
};

const WEBSITE_NODE = {
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: 'TCUBE FITNESS',
  publisher: { '@id': `${SITE_URL}/#organization` },
};

const buildPageNode = (type, fullUrl, name, description, article) => {
  if (type === 'service') {
    return [
      {
        '@type': 'MedicalProcedure',
        '@id': `${fullUrl}#service`,
        name,
        description,
        url: fullUrl,
        provider: { '@id': `${SITE_URL}/#organization` },
        procedureType: 'TherapeuticProcedure',
      },
      {
        '@type': 'Service',
        '@id': `${fullUrl}#offering`,
        name,
        description,
        url: fullUrl,
        provider: { '@id': `${SITE_URL}/#organization` },
        areaServed: { '@type': 'Country', name: 'India' },
        serviceType: 'Doctor-led clinical nutrition & preventive health',
      },
    ];
  }
  if (type === 'calculator') {
    return [
      {
        '@type': 'WebApplication',
        '@id': `${fullUrl}#calculator`,
        name,
        description,
        url: fullUrl,
        applicationCategory: 'HealthApplication',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR' },
        provider: { '@id': `${SITE_URL}/#organization` },
      },
    ];
  }
  if (type === 'article' && article) {
    return [
      {
        '@type': 'MedicalWebPage',
        '@id': `${fullUrl}#article`,
        url: fullUrl,
        name: article.headline || name,
        headline: article.headline || name,
        description,
        image: article.image || `${SITE_URL}/og-image.jpg`,
        datePublished: article.datePublished,
        dateModified: article.dateModified || article.datePublished,
        author: { '@type': 'Person', name: article.author || 'Dr. Pooja Sree' },
        publisher: { '@id': `${SITE_URL}/#organization` },
        mainEntityOfPage: fullUrl,
      },
    ];
  }
  return [
    {
      '@type': 'WebPage',
      '@id': `${fullUrl}#webpage`,
      url: fullUrl,
      name,
      description,
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#organization` },
    },
  ];
};

/**
 * SEOSchema injects a consolidated JSON-LD <script> into document.head
 * via useEffect, so each route emits exactly ONE additional schema script
 * with an @graph containing Organization + WebSite + page-specific nodes
 * + optional Breadcrumb + optional FAQ.
 */
const SEOSchema = ({
  type = 'webpage',
  name,
  description,
  url = '/',
  breadcrumbs,
  faq,
  article,
}) => {
  useEffect(() => {
    const fullUrl = url.startsWith('http') ? url : `${SITE_URL}${url}`;

    // Per-page Open Graph / Twitter meta (upsert the static tags from index.html).
    const ogTitle = name
      ? (name.includes('TCUBE FITNESS') ? name : `${name} | TCUBE FITNESS™`)
      : document.title;
    const upsertMeta = (key, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[property="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    upsertMeta('og:title', ogTitle);
    upsertMeta('og:description', description);
    upsertMeta('og:url', fullUrl);
    upsertMeta('og:type', type === 'article' ? 'article' : 'website');
    upsertMeta('twitter:title', ogTitle);
    upsertMeta('twitter:description', description);
    upsertMeta('twitter:url', fullUrl);

    const graph = [
      ORG_NODE,
      WEBSITE_NODE,
      ...buildPageNode(type, fullUrl, name, description, article),
    ];

    if (breadcrumbs && breadcrumbs.length > 0) {
      graph.push({
        '@type': 'BreadcrumbList',
        '@id': `${fullUrl}#breadcrumb`,
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: b.path.startsWith('http') ? b.path : `${SITE_URL}${b.path}`,
        })),
      });
    }

    if (faq && faq.length > 0) {
      graph.push({
        '@type': 'FAQPage',
        '@id': `${fullUrl}#faq`,
        mainEntity: faq.map((q) => ({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: { '@type': 'Answer', text: q.answer },
        })),
      });
    }

    const jsonLd = { '@context': 'https://schema.org', '@graph': graph };
    const scriptId = 'tcube-seo-schema-graph';

    let scriptEl = document.getElementById(scriptId);
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.type = 'application/ld+json';
      scriptEl.id = scriptId;
      document.head.appendChild(scriptEl);
    }
    scriptEl.textContent = JSON.stringify(jsonLd);

    return () => {
      // Leave the script in place during route transitions; the next
      // SEOSchema mount will rewrite the textContent. Only remove on
      // unmount where no replacement happens within a tick.
      // (no-op here intentionally to avoid flash-of-empty-schema)
    };
  }, [type, name, description, url, breadcrumbs, faq, article]);

  return null;
};

export default SEOSchema;
